import './queryBuilder.less';

import React, { useContext } from 'react';
import { observer } from 'mobx-react';
import moment from 'moment';
import b_ from 'b_';

import { i18n } from 'controls/queryBuilder/localization';
import {
	addEmptyGroup,
	addEmptyRule,
	FieldType,
	GroupConjunction,
	removeRule,
	RuleDefinitionType,
} from 'controls/queryBuilder/ruleDefinition';
import { AntSwitch } from 'controls/react/ant/antSwitch';
import { AntRadioGroup } from 'controls/react/ant/antRadio';
import { AntButton } from 'controls/react/ant/antButton';
import { DeleteOutlined } from '@ant-design/icons';
import { AntSelect } from 'controls/react/ant/ant-select/antSelect';
import { linkModel } from 'framework/mobx-integration';
import { AntInput } from 'controls/react/ant/antInput';
import { AntInputNumber } from 'controls/react/ant/antInputNumber';
import { AntDatePicker } from 'controls/react/ant/antDatePicker';
import { ApplicationState } from 'framework/applicationState';
import { fieldNamesIdName, fieldNamesTitleValue } from 'controls/react/ant/ant-select/types';
import { useStore } from 'core/react/useStore';
import { QueryBuilderProps } from 'controls/queryBuilder/types';
import { QueryBuilderStore } from 'controls/queryBuilder/queryBuilderStore';
import { QueryBuilderContext } from 'controls/queryBuilder/context';
import { isListOperator } from 'controls/queryBuilder/utils/isListOperator';
import { isListValue } from 'controls/queryBuilder/utils/isListValue';
import { VariableBinding } from 'controls/react/variable-binding/variableBinding';
import { VariablePicker } from 'controls/react/variable-binding/components/variable-picker/variablePicker';
import { VariableLabel } from 'controls/react/variable-binding/components/variable-label/variableLabel';
import { createVariableToken } from 'controls/react/variable-binding/utils/createVariableToken';
import { getVariableTokenId } from 'controls/react/variable-binding/utils/getVariableTokenId';

const bBuilder = b_.with('query-builder');
export const QueryBuilder = observer((props: QueryBuilderProps) => {
	const store = useStore(() => new QueryBuilderStore(), { props });

	return (
		<QueryBuilderContext.Provider value={store}>
			<div className={bBuilder()}>
				<QueryBuilderGroup rule={props.rule} config={props.config} variables={props.variables} />
			</div>
		</QueryBuilderContext.Provider>
	);
});

const bGroup = b_.with('query-builder-group');
export const QueryBuilderGroup = observer((props: QueryBuilderProps & { deleteEntry?: () => void }) => {
	const rule = props.rule;

	const andOrDataList = React.useMemo(
		() => [
			{
				name: i18n('AND'),
				id: GroupConjunction.And,
			},
			{
				name: i18n('OR'),
				id: GroupConjunction.Or,
			},
		],
		[],
	);

	const addEmptyRuleWrapper = React.useCallback(() => {
		addEmptyRule(rule);
	}, [rule]);

	const addEmptyGroupWrapper = React.useCallback(() => {
		addEmptyGroup(rule);
	}, [rule]);

	return (
		<div className={bGroup()}>
			<div className={bGroup('buttons-toolbar')}>
				<AntSwitch
					className={bGroup('not-switch')}
					checkedChildren={i18n('Not')}
					unCheckedChildren={i18n('Not')}
					value={rule.properties.not}
					onChange={(v) => (rule.properties.not = v)}
				/>

				<AntRadioGroup<GroupConjunction>
					className={bGroup('and-or-switch')}
					dataList={andOrDataList}
					value={rule.properties.conjunction}
					onChange={(v) => (rule.properties.conjunction = v)}
				/>

				<AntButton className={bGroup('add-rule-button')} type={'primary'} onClick={addEmptyRuleWrapper}>
					{i18n('Add Rule')}
				</AntButton>

				<AntButton className={bGroup('add-group-button')} type={'primary'} onClick={addEmptyGroupWrapper}>
					{i18n('Add Group')}
				</AntButton>
				{props.deleteEntry && (
					<AntButton
						className={bGroup('delete-group-button')}
						icon={<DeleteOutlined />}
						onClick={props.deleteEntry}
					/>
				)}
			</div>
			<div className={bGroup('rules')}>
				{Object.values(rule.children1).map((subRule) =>
					subRule.type == RuleDefinitionType.Rule ? (
						<QueryBuilderRule
							rule={subRule}
							config={props.config}
							variables={props.variables}
							key={subRule.id}
							deleteEntry={() => removeRule(rule, subRule)}
						/>
					) : (
						<QueryBuilderGroup
							rule={subRule}
							config={props.config}
							variables={props.variables}
							key={subRule.id}
							deleteEntry={() => removeRule(rule, subRule)}
						/>
					),
				)}
			</div>
		</div>
	);
});

const bRule = b_.with('query-builder-rule');
export const QueryBuilderRule = observer((props: QueryBuilderProps & { deleteEntry: () => void }) => {
	return (
		<div className={bRule()}>
			<div className={bRule('field')}>
				<QueryBuilderRuleField {...props} />
			</div>
			<div className={bRule('operator')}>
				<QueryBuilderRuleOperator {...props} />
			</div>
			<div className={bRule('value')}>
				<QueryBuilderRuleValue bounced={1000} {...props} />
			</div>
			<div className={bRule('delete')}>
				<AntButton icon={<DeleteOutlined />} onClick={props.deleteEntry} />
			</div>
		</div>
	);
});

const bField = b_.with('query-builder-rule-field');
export const QueryBuilderRuleField = observer((props: QueryBuilderProps) => {
	const rule = props.rule;

	const fields = React.useMemo(() => {
		return Object.keys(props.config).map((x) => ({
			label: props.config[x].label,
			value: x,
		}));
	}, [props.config]);

	return (
		<AntSelect options={fields} className={bField()} showSearch={true} {...linkModel(rule.properties, 'field')} />
	);
});

const bOperator = b_.with('query-builder-rule-operator');
export const QueryBuilderRuleOperator = observer((props: QueryBuilderProps) => {
	if (!props.rule.properties.field) return null;

	const config = props.config[props.rule.properties.field];

	if (config == null) return null;

	const linkedModel = linkModel(props.rule.properties, 'operator');

	const onChangeWrapper = (value: string) => {
		const properties = props.rule.properties;

		if ((config.type == FieldType.MultiSelect && value == 'like') || properties.operator == 'like') {
			properties.value = [];
		} else if (isListOperator(value) != isListOperator(properties.operator)) {
			// the server takes a list the same as a text with its values joined by commas
			properties.value = isListOperator(value)
				? properties.value
						.flatMap((x) => String(x).split(','))
						.map((x) => x.trim())
						.filter((x) => x != '')
				: joinValues(properties.value);
		}

		linkedModel.onChange(value);
	};

	return (
		<AntSelect
			options={config.operators}
			className={bOperator()}
			value={linkedModel.value}
			onChange={onChangeWrapper}
			fieldNames={fieldNamesTitleValue}
		/>
	);
});

const bValue = b_.with('query-builder-rule-value');
type QueryBuilderRuleValueProps = QueryBuilderProps & {
	onPressEnter?: () => void;
	onPressEsc?: () => void;
	bounced?: number;
	forceOneLineLayout?: boolean;
};

// a variable takes the place of the whole value, or of one value of a list
export const QueryBuilderRuleValue = observer((props: QueryBuilderRuleValueProps) => {
	const { field, operator, value } = props.rule.properties;
	const config = props.config[field];
	if (config == null || !operator) {
		return null;
	}

	const variables = props.variables ?? null;
	if (isListValue(config.type, operator)) {
		const list = value as string[];
		const add = (id: string) => {
			const token = createVariableToken(id);
			if (!list.includes(token)) {
				props.rule.properties.value = [...list, token];
			}
		};

		return (
			<VariablePicker variables={variables} type={config.type} list={true} onPick={add}>
				<QueryBuilderListValue {...props} />
			</VariablePicker>
		);
	}

	return (
		<VariableBinding
			variables={variables}
			type={config.type}
			variableId={getVariableTokenId(value[0])}
			onVariableChange={(id) => (props.rule.properties.value = id == null ? [] : [createVariableToken(id)])}
		>
			<QueryBuilderSingleValue {...props} />
		</VariableBinding>
	);
});

const QueryBuilderSingleValue = observer((props: QueryBuilderRuleValueProps) => {
	const config = props.config[props.rule.properties.field];

	const setSingleValue = React.useCallback(
		(newValue: string) => {
			props.rule.properties.value[0] = newValue;
		},
		[props.rule],
	);

	const clearSelection = React.useCallback(() => {
		setSingleValue(null);
	}, [setSingleValue]);

	const setSingleNumberValue = React.useCallback(
		(value: string | number) => {
			props.rule.properties.value[0] = value;
		},
		[props.rule],
	);

	const setDateTimeValue = React.useCallback(
		(value: moment.Moment) => {
			if (value) {
				props.rule.properties.value[0] = value.valueOf();
			} else {
				setMultipleValue([]);
			}
		},
		[props.rule],
	);

	const setMultipleValue = React.useCallback(
		(newValue: string[]) => {
			props.rule.properties.value = newValue;
		},
		[props.rule],
	);

	const firstValue = props.rule.properties.value.length > 0 ? props.rule.properties.value[0] : '';

	let firstStringValue: string;
	let firstNumberValue: number;

	if (typeof firstValue == 'number') {
		firstNumberValue = firstValue;
	} else {
		firstStringValue = firstValue;
	}

	switch (config.type) {
		case FieldType.Select:
			return (
				<AntSelect
					className={bValue()}
					options={config.fieldSettings.listValues}
					fieldNames={fieldNamesTitleValue}
					onClear={clearSelection}
					allowClear={true}
					closeOnMouseLeave={true}
					value={firstStringValue}
					onChange={setSingleValue}
				/>
			);

		case FieldType.Boolean:
			return <BooleanEditor {...props} />;

		case FieldType.Date:
			return (
				<AntDatePicker
					className={bValue()}
					format={ApplicationState.dateTimeFormat}
					showTime={true}
					allowClear={true}
					placeholder={''}
					value={firstValue ? moment(firstNumberValue) : null}
					onChange={setDateTimeValue}
				/>
			);

		case FieldType.Number:
			return (
				<AntInputNumber
					className={bValue()}
					value={firstValue}
					bounced={props.bounced}
					onPressEnter={props.onPressEnter}
					onPressEsc={props.onPressEsc}
					onChange={setSingleNumberValue}
				/>
			);

		default:
			return (
				<AntInput
					className={bValue()}
					value={firstStringValue}
					bounced={props.bounced}
					onPressEnter={props.onPressEnter}
					onPressEsc={props.onPressEsc}
					onChange={setSingleValue}
				/>
			);
	}
});

const QueryBuilderListValue = observer((props: QueryBuilderRuleValueProps) => {
	const store = useContext(QueryBuilderContext);
	const { field, value } = props.rule.properties;
	const config = props.config[field];
	const loaded = config.fieldSettings.listValues.length == 0 ? store?.getListValues(field) : undefined;
	const valueOptions = (loaded?.values ?? config.fieldSettings.listValues).map((entry) => ({
		value: entry.value,
		label: entry.title,
	}));
	const variableOptions = (value as string[]).flatMap((item) => {
		const id = getVariableTokenId(item);
		const variable = props.variables?.find((x) => x.id == id);
		return id == null ? [] : [{ value: item, label: <VariableLabel variable={variable} /> }];
	});

	return (
		<AntSelect
			className={bValue()}
			options={[...valueOptions, ...variableOptions]}
			loading={loaded?.loading}
			mode={'multiple'}
			closeOnMouseLeave={true}
			layout={props.forceOneLineLayout ? 'one-line' : null}
			value={value as string[]}
			onChange={(newValue: string[]) => (props.rule.properties.value = newValue)}
		/>
	);
});

// a variable can't be joined with the other values: it is kept only when there are none
function joinValues(values: (string | number)[]): string[] {
	const fixed = values.filter((x) => getVariableTokenId(x) == null);
	return fixed.length == 0 && values.length > 0 ? [String(values[0])] : [fixed.join(',')];
}

const BooleanEditor = observer((props: QueryBuilderProps) => {
	const value = props.rule.properties.value.length > 0 ? props.rule.properties.value[0] : '';

	const dataListShort = React.useMemo(
		() => [
			{
				value: 'true',
				label: i18n('Yes'),
			},
			{
				value: 'false',
				label: i18n('No'),
			},
		],
		[],
	);

	return (
		<AntSelect
			value={value}
			className={bValue()}
			placeholder={i18n('Select...')}
			options={dataListShort}
			onChange={(value) => {
				props.rule.properties.value[0] = value;
			}}
		/>
	);
});
